import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import Model from '@ckeditor/ckeditor5-ui/src/model';
import { createDropdown, addListToDropdown } from '@ckeditor/ckeditor5-ui/src/dropdown/utils';
import { getLocalizedOptions } from './utils';
import Collection from '@ckeditor/ckeditor5-utils/src/collection';
import Dialog from '@ckeditor/ckeditor5-ui/src/dialog/dialog';
import View from '@ckeditor/ckeditor5-ui/src/view';
import './CkeditorUI.css';
import { renderModalContainer } from './EditorModal';
import $ from 'jquery';

/**
 * CopilotUI is a CKEditor plugin that provides a user interface for interacting with 
 * various Copilot AI functionalities such as writing prompts, auto-rewriting, summarizing, 
 * and adjusting tones. It integrates with the CKEditor dialog plugin and provides a dropdown 
 * menu with different options for AI-assisted text manipulation.
 * 
 * @extends Plugin
 */
/**
 * CopilotUI class extends the Plugin class to provide a custom UI component for CKEditor.
 * This class includes methods to initialize the Copilot UI, handle user actions, and manage data persistence.
 * 
 * @class CopilotUI
 * @extends Plugin
 */
export default class CopilotUI extends Plugin {
    static get pluginName() {
        return 'CopilotUI';
    }

    get requires() {
        return [Dialog];
    }

    constructor(editor) {
        super(editor);
    }

    /**
     * Retrieves the selected data from the editor's document selection.
     * Iterates through the items in the selection range and concatenates
     * the data if it is a string.
     *
     * @returns {Promise<string>} A promise that resolves to the concatenated
     * string data from the selection, or an empty string if no string data
     * is found.
     */
    async getSelectedData() {
        const selection = this.editor.model.document.selection;
        const range = selection.getFirstRange();
        let result = "";
        let foundData = false;
        if (range) {
            result = this.editor.data.stringify(this.editor.model.getSelectedContent(this.editor.model.document.selection));
            foundData = true;
        }
        return foundData ? result : '';
    }

    /**
     * Initializes the Copilot UI component for the CKEditor.
     * 
     * This method sets up the dropdown menu for the Copilot AI feature, including the button view,
     * dropdown items, and event listeners for executing actions based on user selection.
     * 
     * @method init
     * @memberof CopilotUI
     * 
     * @example
     * // Initialize the Copilot UI component
     * copilotUI.init();
     * 
     * @fires execute - When an action is executed from the dropdown menu.
     * 
     * @listens document#keepData - Handles the keepData event to manage data persistence.
     */
    init() {
        const editor = this.editor;
        const componentFactory = editor.ui.componentFactory;
        const t = editor.t;
        const options = getLocalizedOptions(editor);

        const defaultTitle = t('Copilot');
        const dropdownTooltip = t('Copilot');

        componentFactory.add('copilotAI', locale => {
            const itemDefinitions = new Collection();

            for (const option of options) {
                const def = {
                    type: 'button',
                    model: new Model({
                        label: option.title,
                        class: option.class,
                        withText: true,
                        labeledKey: option.model
                    })
                };
                itemDefinitions.add(def);
            }

            const dropdownView = createDropdown(locale);
            addListToDropdown(dropdownView, itemDefinitions);

            dropdownView.buttonView.set({
                isOn: false,
                label: defaultTitle,
                withText: true,
                tooltip: dropdownTooltip,
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"><defs><linearGradient id="linear-gradient" x1="-0.212" y1="0.037" x2="0.945" y2="1.142" gradientUnits="objectBoundingBox"><stop offset="0" stop-color="#0837c4" /><stop offset="0.242" stop-color="#0b4bce" /><stop offset="0.738" stop-color="#1071df" /><stop offset="1" stop-color="#127fe6" /></linearGradient><linearGradient id="linear-gradient-2" x1="0.187" y1="1.182" x2="0.672" y2="-0.16" gradientUnits="objectBoundingBox"><stop offset="0" stop-color="#f8c704" /><stop offset="0.065" stop-color="#f1c609" /><stop offset="0.163" stop-color="#dcc319" /><stop offset="0.281" stop-color="#babe31" /><stop offset="0.415" stop-color="#8cb754" /><stop offset="0.561" stop-color="#50ae7f" /><stop offset="0.592" stop-color="#43ac89" /><stop offset="0.617" stop-color="#41ad93" /><stop offset="0.737" stop-color="#37b0be" /><stop offset="0.845" stop-color="#30b2dd" /><stop offset="0.936" stop-color="#2cb4f0" /><stop offset="1" stop-color="#2ab4f7" /></linearGradient><linearGradient id="linear-gradient-3" x1="-0.038" y1="0.511" x2="1.043" y2="0.497" gradientUnits="objectBoundingBox"><stop offset="0" stop-color="#ffb155" /><stop offset="0.008" stop-color="#ffb055" /><stop offset="0.475" stop-color="#eb7447" /><stop offset="0.819" stop-color="#df4e3f" /><stop offset="1" stop-color="#da403c" /></linearGradient><linearGradient id="linear-gradient-4" x1="0.391" y1="1.048" x2="0.716" y2="-0.144" gradientUnits="objectBoundingBox"><stop offset="0" stop-color="#ffb152" /><stop offset="0.531" stop-color="#f36284" /><stop offset="0.994" stop-color="#a355e9" /></linearGradient><clipPath id="clip-Artboard_2"><rect width="22" height="22" /></clipPath></defs><g id="Artboard_2" data-name="Artboard – 2" clip-path="url(#clip-Artboard_2)"><g id="Group_37154" data-name="Group 37154" transform="translate(-317 -230)"><rect id="Rectangle_4919" data-name="Rectangle 4919" width="22" height="22" transform="translate(317 230)" fill="none" /><g id="icons8-microsoft-copilot_1_" data-name="icons8-microsoft-copilot (1)" transform="translate(314.087 226.225)"><path id="Path_32109" data-name="Path 32109" d="M21.676,12.8a2.525,2.525,0,0,1,2.449-1.774h6.147a2.522,2.522,0,0,1-2.124-2.3c-.35-2.149-1.274-3-2.574-3.024s-1.9,1.349-2.049,1.824S21.676,12.8,21.676,12.8Z" transform="translate(-9.263 0)" fill="url(#linear-gradient)" /><path id="Path_32110" data-name="Path 32110" d="M11.894,18l3.149-10.57s.6-1.774,2.024-1.724H9.17A2.982,2.982,0,0,0,6.846,7.256,16.042,16.042,0,0,0,4.472,13.2c-.875,4-.525,4.223-.525,4.223s-.225,2.2,3.149,2.024H9.7A2.451,2.451,0,0,0,11.894,18Z" transform="translate(0 0)" fill="url(#linear-gradient-2)" /><path id="Path_32111" data-name="Path 32111" d="M25.927,6.229l1.29-.211a2.323,2.323,0,0,0-1.07-.3l-.1-.006h-.031a1.7,1.7,0,0,0-.262.02c-.036.005-.071.012-.105.019-.055.012-.108.027-.159.044a1.821,1.821,0,0,0-.619.353,2.364,2.364,0,0,0-.172.167,3.371,3.371,0,0,0-.7,1.218c-.02.065-.073.217-.143.422l-1.158,3.886c.045-.048.1-.093.155-.14.029-.027.056-.054.088-.079s.094-.073.145-.109a2.364,2.364,0,0,1,.28-.174c.013-.007.022-.016.036-.022L24.51,7.579S24.976,6.271,25.927,6.229Z" transform="translate(-9.596 0)" opacity="0.05" /><path id="Path_32112" data-name="Path 32112" d="M25.942,5.97l.77-.177a2.736,2.736,0,0,0-.654-.081,1.865,1.865,0,0,0-.6.106l-.043.016c-.042.017-.083.038-.123.058s-.06.031-.089.047-.049.03-.074.046c-.044.029-.088.06-.13.092l-.026.02a2.7,2.7,0,0,0-.542.61l-.009.014c-.015.023-.028.045-.042.068a3.563,3.563,0,0,0-.343.745c-.009.03-.026.079-.047.143l-1.241,4.164a2.436,2.436,0,0,1,.327-.294l1.2-4.039C24.282,7.5,24.8,6.014,25.942,5.97Z" transform="translate(-9.606 -0.002)" opacity="0.07" /><path id="Path_32113" data-name="Path 32113" d="M18.6,28.607a2.525,2.525,0,0,1-2.449,1.774H10.006a2.522,2.522,0,0,1,2.124,2.3c.35,2.149,1.274,3,2.574,3.024s1.9-1.349,2.049-1.824S18.6,28.607,18.6,28.607Z" transform="translate(-3.189 -11.861)" fill="url(#linear-gradient-3)" /><path id="Path_32114" data-name="Path 32114" d="M18.074,36.035l-1.29.211a2.323,2.323,0,0,0,1.07.3l.1.006h.031a1.7,1.7,0,0,0,.262-.02c.036-.005.071-.012.105-.019.055-.012.108-.027.159-.044a1.822,1.822,0,0,0,.619-.353,2.365,2.365,0,0,0,.172-.167,3.371,3.371,0,0,0,.7-1.218c.02-.065.073-.217.143-.422l1.158-3.886c-.045.048-.1.093-.155.14-.029.027-.056.054-.088.079s-.094.073-.145.109a2.364,2.364,0,0,1-.28.174c-.013.007-.022.016-.036.022L19.49,34.685S19.025,35.993,18.074,36.035Z" transform="translate(-6.582 -12.73)" opacity="0.05" /><path id="Path_32115" data-name="Path 32115" d="M18.572,36.294l-.77.177a2.736,2.736,0,0,0,.654.081,1.865,1.865,0,0,0,.6-.106l.043-.016c.042-.017.083-.038.123-.058s.06-.031.089-.047.049-.03.074-.046c.044-.029.088-.06.13-.092l.026-.02a2.7,2.7,0,0,0,.542-.61l.009-.014c.015-.023.028-.045.042-.068a3.563,3.563,0,0,0,.343-.745c.009-.03.026-.079.048-.143l1.241-4.164a2.436,2.436,0,0,1-.327.294l-1.2,4.039C20.232,34.762,19.71,36.249,18.572,36.294Z" transform="translate(-7.087 -12.729)" opacity="0.07" /><path id="Path_32116" data-name="Path 32116" d="M24.178,17.313,21.01,27.89s-.58,1.767-2,1.717h7.9a2.982,2.982,0,0,0,2.324-1.549A16.042,16.042,0,0,0,31.6,22.111c.875-4,.525-4.223.525-4.223s.225-2.2-3.149-2.024h-2.6A2.45,2.45,0,0,0,24.178,17.313Z" transform="translate(-8.247 -5.765)" fill="url(#linear-gradient-4)" /></g></g></g></svg>',
            });

            dropdownView.extendTemplate({
                attributes: {
                    class: ['ck-copilot-dropdown']
                }
            });

            this.listenTo(dropdownView, 'execute', async (evt) => {
                const source = evt.source;
                const instanceId = editor.config.get('copilot.instanceId');
                if (source.labeledKey && instanceId === editor.id) {
                    switch (source.labeledKey) {
                        case 'writeAPrompt':
                            this._writeAPrompt(source, locale);
                            break;
                        case 'autoRewrite':
                            this._handleAction(source, locale, 'Auto Rewrite with Copilot', 'rewrite');
                            break;
                        case 'summarize':
                            this._handleAction(source, locale, 'Summarize with Copilot', 'summarize');
                            break;
                        case 'businessTone':
                            this._handleAction(source, locale, 'Adjust Business Tone with Copilot', 'adjust business tone');
                            break;
                        case 'legalTone':
                            this._handleAction(source, locale, 'Adjust Legal Tone with Copilot', 'legal tone');
                            break;
                        default:
                            this._writeAPrompt(source, locale);
                    }
                }
            });

            renderModalContainer();
            return dropdownView;
        });

        document.addEventListener('keepData', (event) => {
            const instanceId = editor.config.get('copilot.instanceId');
            if (event.detail.instanceId === instanceId) {
                this._handleKeepData(event, editor);
            }
        });
    }

    async _handleKeepData(event, editor) {
        const { resultData, instanceId } = event.detail;
        if (resultData !== null && resultData !== undefined && instanceId === editor.config.get('copilot.instanceId')) {
            let cleanedResultData = resultData.replace(/\s+/g, ' ').trim();
            cleanedResultData = cleanedResultData.replace(/(<(ul|ol|li|p|div)[^>]*>)(<br\s*\/?>\s*)+(<\/\2>)/g, '$1$4');
            const viewFragment = editor.data.processor.toView(cleanedResultData);
            const modelFragment = editor.data.toModel(viewFragment);

            editor.model.enqueueChange({ isUndoable: true }, writer => {
                const selection = editor.model.document.selection;
                const range = selection.getFirstRange();

                if (range) {
                    writer.remove(range);
                }

                const insertPosition = selection.getFirstPosition();
                editor.model.insertContent(modelFragment, insertPosition);
                writer.setSelection(insertPosition.getShiftedBy(modelFragment.childCount));
            });

            // Ensure the editor is focused after the change
            editor.editing.view.focus();
        }
    }

    async _writeAPrompt(source, locale) {
        const title = source.label;
        const labeledKey = source.labeledKey;
        const label = 'Draft with Copilot';
        let selectedData = await this.getSelectedData();
        const aICommand = "writeAPrompt";
        const showModalButton = document.getElementById('showWriteAPromptButton');
        const instanceId = this.editor.config.get('copilot.instanceId'); // Get the instanceId from the editor config
        if (showModalButton) {
            const event = new CustomEvent('showWriteAPromptModal', { detail: { title, labeledKey, label, selectedData, aICommand, instanceId } });
            showModalButton.dispatchEvent(event);
        } else {
            console.error('showWriteAPromptButton element not found');
        }
    }

    async _createDialogAlert(locale) {
        const dialog = this.editor.plugins.get('Dialog');
        if (!dialog) {
            console.error('Dialog plugin is not loaded');
            return;
        }

        if (typeof View === 'undefined') {
            console.error('View is not defined');
            return;
        }

        const textView = new View(locale);

        textView.setTemplate({
            tag: 'div',
            attributes: {
                class: 'ck ck-dialog__content ck-copilot-dialog-alert-content',
                style: {
                    padding: 'var(--ck-spacing-large)',
                },
                tabindex: -1
            },
            children: ["No selected data found"]
        });

        if (dialog) {
            dialog.show({
                isModal: true,
                className: 'ck-copilot-dialog-alert',
                title: 'Copilot',
                content: textView,
                actionButtons: [
                    {
                        label: 'Discard',
                        class: 'ck-copilot-discard',
                        withText: true,
                        onExecute: () => dialog.hide()
                    }
                ],
                onHide() { }
            });
        }
    }

    /**
     * Handles the action triggered by the source element.
     *
     * @param {Object} source - The source object containing the label and labeledKey.
     * @param {string} locale - The locale string.
     * @param {string} label - The label string.
     * @param {string} aICommand - The AI command string.
     * @returns {Promise<void>} - A promise that resolves when the action is handled.
     * @private
     */
    async _handleAction(source, locale, label, aICommand) {
        const title = source.label;
        const labeledKey = source.labeledKey;
        const selectedData = await this.getSelectedData();
        const instanceId = this.editor.config.get('copilot.instanceId'); // Get the instanceId from the editor config
        if (selectedData === '') {
            this._createDialogAlert(locale);
        } else {
            const showModalButton = document.getElementById('showLabelButton');
            if (showModalButton) {
                const event = new CustomEvent('showAutoModal', { detail: { title, labeledKey, label, selectedData, aICommand, instanceId } });
                showModalButton.dispatchEvent(event);
            } else {
                console.error('showLabelButton element not found');
            }
        }
    }
}
